import React from "react";
import { Package, Eye, MousePointerClick } from "lucide-react";

interface ProductPerformanceItem {
  entityId: number;
  views: number;
  clicks: number;
  clickThroughRate: number;
}

interface ProductPerformanceProps {
  products?: ProductPerformanceItem[];
}

const ProductPerformance: React.FC<ProductPerformanceProps> = ({
  products = [],
}) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* HEADER */}

      <div className="px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center">
            <Package size={18} />
          </div>

          <div>
            <h2 className="text-sm font-bold text-gray-800">
              Most Viewed Products
            </h2>

            <p className="text-[10px] text-gray-400 mt-0.5">
              Products receiving the most customer attention
            </p>
          </div>
        </div>
      </div>

      {/* TABLE */}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50/70">
              <th className="px-5 py-3 text-left text-[10px] font-semibold text-gray-400">
                Product
              </th>

              <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400">
                Views
              </th>

              <th className="px-4 py-3 text-right text-[10px] font-semibold text-gray-400">
                Clicks
              </th>

              <th className="px-5 py-3 text-right text-[10px] font-semibold text-gray-400">
                CTR
              </th>
            </tr>
          </thead>

          <tbody>
            {products.length > 0 ? (
              products.map((product, index) => (
                <tr
                  key={product.entityId}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors"
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center text-[10px] font-bold">
                        {index + 1}
                      </span>

                      <div>
                        <p className="text-xs font-semibold text-gray-700">
                          Product #{product.entityId}
                        </p>

                        <p className="text-[9px] text-gray-400 mt-0.5">
                          Product engagement
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                      <Eye size={13} className="text-violet-500" />

                      {product.views.toLocaleString()}
                    </div>
                  </td>

                  <td className="px-4 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                      <MousePointerClick
                        size={13}
                        className="text-emerald-500"
                      />

                      {product.clicks.toLocaleString()}
                    </div>
                  </td>

                  <td className="px-5 py-3.5 text-right">
                    <span className="text-xs font-bold text-violet-600">
                      {product.clickThroughRate.toFixed(2)}%
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="py-12 text-center">
                  <p className="text-xs text-gray-400">
                    No product activity available.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductPerformance;
